import React, { Fragment, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { AiFillFileAdd } from "react-icons/ai"
import { toast } from "react-toastify"
import heic2any from "heic2any"
import clsx from "clsx"

import FuwDeviceHeicModal from "./FuwDeviceHeicModal"
import FuwFooter from "./FuwFooter"
import classes from "./FuwDevice.module.scss"

const validMimeType = ["image/jpeg", "image/png", "image/tiff", "image/webp", "image/heic", "image/heif"]

const FuwDevice = props => {
  const {
    setTab,
    setFiles,
    numberOfFiles,
    originalFiles,
    maxNumberOfUploads
  } = props

  const [t] = useTranslation()

  const [totalConvertedHeicImages, setTotalConvertedHeicImages] = useState(0)
  const [convertedHeicImages, setConvertedHeicImages] = useState(0)
  const [openConvertingHeicImagesModal, setOpenConvertingHeicImagesModal] = useState(false)

  const fileInputRef = useRef(null)
  const unmount = useRef(false)
  const cancelConvertingHeicImagesRef = useRef(false)

  const isHeicImage = (file) => {
    const fileName = file.name.toLowerCase()

    return (
      fileName.includes("heic") ||
      fileName.includes("heif")
    )
  }

  const getHeicImageFileName = (file) => {
    if (file.name.includes(".HEIC")) {
      return file.name.split(".HEIC")[0]
    }

    if (file.name.includes(".heic")) {
      return file.name.split(".heic")[0]
    }

    if (file.name.includes(".heif")) {
      return file.name.split(".heif")[0]
    }

    if (file.name.includes(".HEIF")) {
      return file.name.split(".HEIF")[0]
    }

    return file.name
  }

  const handleClick = () => {
    const uploader = fileInputRef.current
    uploader.value = ""
    uploader.click()
  }

  const convertHeicFilesToJpeg = async (files) => {
    const numberOfHeicImages =
      files.filter(file => isHeicImage(file)).length

    if (numberOfHeicImages === 0) return files

    const results = []

    setOpenConvertingHeicImagesModal(true)

    setConvertedHeicImages(0)

    setTotalConvertedHeicImages(numberOfHeicImages)

    cancelConvertingHeicImagesRef.current = false

    for (const file of files) {
      if (!isHeicImage(file)) {
        results.push(file)

        continue
      }

      const blob =
        await heic2any({ blob: file, toType: "image/jpeg" })

      if (unmount.current) return

      if (cancelConvertingHeicImagesRef.current) return

      const heicFileName =
        getHeicImageFileName(file)

      const fileOfBlob = new File([blob], `${heicFileName}.jpeg`)

      results.push(fileOfBlob)

      setConvertedHeicImages(prev => prev += 1)
    }

    setOpenConvertingHeicImagesModal(false)

    return results
  }

  const handleFiles = async (targetFiles) => {
    const slicedFiles =
      targetFiles.slice(0, maxNumberOfUploads)

    const filteredFiles = []

    slicedFiles.forEach(file => {
      if (file.type && !validMimeType.includes(file.type)) return

      if (!file.type && !isHeicImage(file)) return

      filteredFiles.push(file)
    })

    if (filteredFiles.length === 0) {
      toast.error(t("فایل_ها_باید_jpg_یا_tiff_یا_png_یا_webp_یا_heic_باشند"))

      return
    }

    const convertedFiles =
      await convertHeicFilesToJpeg(
        filteredFiles
      )

    if (unmount.current) return

    if (cancelConvertingHeicImagesRef.current) return

    const results = []

    for (const file of convertedFiles) {
      results.push({
        id: (Math.random() * 10000) | 0,
        edited: false,
        isFile: true,
        blob: file,
        url: URL.createObjectURL(file),
        name: file.name,
        size: file.size,
        type: file.type,
        uploadProgress: 0,
        uploadFailed: false
      })
    }

    setTab("files")

    setFiles(prev => {
      const newFiles = [...prev, ...results]

      originalFiles.current = newFiles

      return newFiles
    })
  }

  useEffect(() => {
    unmount.current = false

    return () => {
      unmount.current = true
    }
  }, [])

  const showFooter = numberOfFiles > 0

  return (
    <Fragment>
      <div
        className={clsx(
          classes["container"],
          showFooter && classes["shrink"]
        )}
      >
        <div
          onDragOver={(e) => e.preventDefault()}
          onClick={handleClick}
          className={classes["wrapper"]}
          onDrop={(e) => {
            e.preventDefault()

            handleFiles([...e.dataTransfer.files])
          }}
        >
          <AiFillFileAdd
            size={50}
            className={classes["icon"]}
          />
          <div className={classes["title"]}>
            {t("فایل_مورد_نظر_خود_را_انتخاب_کنید")}
          </div>
        </div>
      </div>

      {
        numberOfFiles > 0 &&
        <FuwFooter
          setTab={setTab}
          numberOfFiles={numberOfFiles}
        />
      }

      <FuwDeviceHeicModal
        convertedHeicImages={convertedHeicImages}
        cancelConvertingHeicImagesRef={cancelConvertingHeicImagesRef}
        totalConvertedHeicImages={totalConvertedHeicImages}
        openConvertingHeicImagesModal={openConvertingHeicImagesModal}
        setOpenConvertingHeicImagesModal={setOpenConvertingHeicImagesModal}
      />

      <input
        hidden
        type="file"
        multiple={maxNumberOfUploads > 1}
        ref={fileInputRef}
        accept="image/jpeg, image/png, image/tiff, image/heic, image/heif"
        onChange={(e) => {
          e.preventDefault()

          handleFiles([...e.target.files])
        }}
      />
    </Fragment>
  )
}

export default FuwDevice